import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCart } from '@/contexts/CartContext';
import { useMiniCart } from '@/contexts/MiniCartContext';
import { BUNDLE, getCatalogItem, type CatalogId } from '@/lib/products';
import { BUNDLE_PRODUCT_IDS, getBundleUnitPrice } from '@/lib/product-catalog-data';
import { getShopView, formatPriceEur } from '@/lib/shopView';

/** "Mach's komplett": offers swapping the just-added single product for one set. */
export const SetUpgradeModal: React.FC = () => {
  const { t, language } = useLanguage();
  const u = t.shop.upgrade;
  const { addItem } = useCart();
  const { upgradeFor, finishUpgrade } = useMiniCart();
  const fmt = (v: number) => formatPriceEur(v, language);

  const single = upgradeFor ? getShopView(upgradeFor as CatalogId, language, '') : null;
  const set = getShopView('bundle', language, t.products.bundle.shortDescription);
  const setPrice = getBundleUnitPrice(1);
  const saved = Math.round((BUNDLE.msrp - setPrice) * 100) / 100;
  const extra = single ? Math.round((setPrice - single.price) * 100) / 100 : 0;
  const others = (BUNDLE_PRODUCT_IDS as string[])
    .filter((id) => id !== upgradeFor)
    .map((id) => getCatalogItem(id as CatalogId)?.name[language].replace(/^Daily /, ''))
    .join(u.andWord);

  const upgrade = () => {
    const it = getCatalogItem('bundle');
    // Adding the set replaces single products and clears any promo code (CartContext).
    if (it) addItem(it, 1);
    finishUpgrade();
  };

  return (
    <Dialog open={!!single} onOpenChange={(o) => { if (!o) finishUpgrade(); }}>
      <DialogContent className="max-h-[92vh] w-[calc(100%-2rem)] max-w-xl overflow-y-auto rounded-2xl p-5 sm:p-6" data-set-upgrade>
        <DialogTitle className="pr-8 font-dm-serif text-3xl font-normal">{u.title}</DialogTitle>
        <DialogDescription className="sr-only">{single ? u.extra.replace('{amount}', fmt(extra)).replace('{products}', others) : ''}</DialogDescription>
        {single && set && (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl border border-border p-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{u.yourChoice}</p>
                <img src={single.image} loading="lazy" alt="" aria-hidden="true" className="mb-2 aspect-square w-full rounded-lg object-cover" />
                <p className="font-dm-serif text-lg leading-tight">{single.name}</p>
                <p className="text-sm">{fmt(single.price)}</p>
              </div>
              <div className="relative rounded-xl border-2 border-foreground bg-ci-warm-white p-3">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-ci-orange-deep">{u.recommended}</p>
                <img src={set.image} loading="lazy" alt="" aria-hidden="true" className="mb-2 aspect-square w-full rounded-lg object-cover" />
                <p className="font-dm-serif text-lg leading-tight">{set.name}</p>
                <p className="text-sm font-semibold">{fmt(setPrice)}</p>
                <p className="mt-1 text-xs font-medium text-ci-orange-deep">{u.saved.replace('{amount}', fmt(saved))}</p>
              </div>
            </div>
            <p className="text-center text-[15px] font-semibold" data-upgrade-extra>
              {u.extra.replace('{amount}', fmt(extra)).replace('{products}', others)}
            </p>
            <button type="button" onClick={upgrade}
              className="inline-flex h-14 w-full items-center justify-center rounded-full bg-h2h-charcoal text-base font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
              {u.cta}
            </button>
            <button type="button" onClick={finishUpgrade}
              className="mx-auto min-h-11 text-sm text-muted-foreground underline underline-offset-4 hover:text-foreground">
              {u.decline.replace('{product}', single.name)}
            </button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
